/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ComponentFactoryResolver, ComponentRef, Directive, Injector, Input, NgModuleFactory, NgModuleRef, OnChanges, OnDestroy, Provider, SimpleChanges, Type, ViewContainerRef} from '@angular/core';
/**
 * Instantiates a single {\@link Component} type and inserts its Host View into current View.
 * `NgComponentOutlet` provides a declarative approach for dynamic component creation.
 * 
 * `NgComponentOutlet` requires a component type, if a falsy value is set the view will clear and
 * any existing component will get destroyed.
 * 
 * ### Fine tune control
 * 
 * You can control the component creation process by using the following optional attributes:
 * 
 * * `ngComponentOutletInjector`: Optional custom {\@link Injector} that will be used as parent for
 * the Component. Defaults to the injector of the current view container.
 * 
 * * `ngComponentOutletContent`: Optional list of projectable nodes to insert into the content
 * section of the component, if exists.
 * 
 * * `ngComponentOutletNgModuleFactory`: Optional module factory to allow dynamically loading other
 * module, then load a component from that module.
 * 
 * ### Syntax
 * 
 * Simple
 * ```
 * <ng-container *ngComponentOutlet="componentTypeExpression"></ng-container>
 * ```
 * 
 * Customized injector/content
 * ```
 * <ng-container *ngComponentOutlet="componentTypeExpression;
 *                                   injector: injectorExpression;
 *                                   content: contentNodesExpression;">
 * </ng-container>
 * ```
 * 
 * Customized ngModuleFactory
 * ```
 * <ng-container *ngComponentOutlet="componentTypeExpression;
 *                                   ngModuleFactory: moduleFactory;">
 * </ng-container>
 * ```
 * ## Example
 * 
 * {\@example common/ngComponentOutlet/ts/module.ts region='SimpleExample'}
 * 
 * A more complete example with additional options:
 * 
 * {\@example common/ngComponentOutlet/ts/module.ts region='CompleteExample'}
 * A more complete example with ngModuleFactory:
 * 
 * {\@example common/ngComponentOutlet/ts/module.ts region='NgModuleFactoryExample'}
 * 
 * \@experimental
 */
export class NgComponentOutlet implements OnChanges, OnDestroy {
   ngComponentOutlet: Type<any>;
   ngComponentOutletInjector: Injector;
   ngComponentOutletContent: any[][];
   ngComponentOutletNgModuleFactory: NgModuleFactory<any>;
private _componentRef: ComponentRef<any>|null = null;
private _moduleRef: NgModuleRef<any>|null = null;
/**
 * @param {?} _viewContainerRef
 */
constructor(private _viewContainerRef: ViewContainerRef) {}
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    this._viewContainerRef.clear();
    this._componentRef = null;

    if (this.ngComponentOutlet) {
      const /** @type {?} */ elInjector = this.ngComponentOutletInjector || this._viewContainerRef.parentInjector;

      if (changes['ngComponentOutletNgModuleFactory']) {
        if (this._moduleRef) this._moduleRef.destroy();

        if (this.ngComponentOutletNgModuleFactory) {
          const /** @type {?} */ parentModule = elInjector.get(NgModuleRef);
          this._moduleRef = this.ngComponentOutletNgModuleFactory.create(parentModule.injector);
        } else {
          this._moduleRef = null;
        }
      }

      const /** @type {?} */ componentFactoryResolver = this._moduleRef ? this._moduleRef.componentFactoryResolver :
                                                         elInjector.get(ComponentFactoryResolver);

      const /** @type {?} */ componentFactory =
          componentFactoryResolver.resolveComponentFactory(this.ngComponentOutlet);

      this._componentRef = this._viewContainerRef.createComponent(
          componentFactory, this._viewContainerRef.length, elInjector,
          this.ngComponentOutletContent);
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._moduleRef) this._moduleRef.destroy();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: '[ngComponentOutlet]'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ViewContainerRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'ngComponentOutlet': [{ type: Input },],
'ngComponentOutletInjector': [{ type: Input },],
'ngComponentOutletContent': [{ type: Input },],
'ngComponentOutletNgModuleFactory': [{ type: Input },],
};
}

function NgComponentOutlet_tsickle_Closure_declarations() {
/** @type {?} */
NgComponentOutlet.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NgComponentOutlet.ctorParameters;
/** @type {?} */
NgComponentOutlet.propDecorators;
/** @type {?} */
NgComponentOutlet.prototype.ngComponentOutlet;
/** @type {?} */
NgComponentOutlet.prototype.ngComponentOutletInjector;
/** @type {?} */
NgComponentOutlet.prototype.ngComponentOutletContent;
/** @type {?} */
NgComponentOutlet.prototype.ngComponentOutletNgModuleFactory;
/** @type {?} */
NgComponentOutlet.prototype._componentRef;
/** @type {?} */
NgComponentOutlet.prototype._moduleRef;
/** @type {?} */
NgComponentOutlet.prototype._viewContainerRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/common/src/directives/ng_component_outlet.ts